import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { Provider } from "react-redux";
import { store } from "./store";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ChildProvider } from "./contexts/ChildContext";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import { FeedbackProvider } from "./contexts/FeedbackContext";
import { ToastProvider, useToastContext } from "./contexts/ToastContext";
import ToastContainer from "./components/ToastContainer";
import ToastNotifications from "./components/ToastNotifications";

import WebSocketStatus from "./components/WebSocketStatus";
import RealtimeNotifications from "./components/RealtimeNotifications";
import SessionWarning from "./components/common/SessionWarning";
import HelpCenter from "./components/common/HelpCenter";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";

// Performance monitoring
import { logBundleInfo, monitorMemoryUsage } from "./utils/performance";

// Lazy load page components for better performance
import { lazyLoad } from "./utils/lazyLoading.jsx";

const HomePage = lazyLoad(() => import("./pages/HomePage"));
const LoginPage = lazyLoad(() => import("./pages/LoginPage"));
const RegisterPage = lazyLoad(() => import("./pages/RegisterPage"));
const DashboardPage = lazyLoad(() => import("./pages/DashboardPage"));
const ProfilePage = lazyLoad(() => import("./pages/ProfilePage"));
const SharingPage = lazyLoad(() => import("./pages/SharingPage"));
const MilestonesPage = lazyLoad(() => import("./pages/MilestonesPage"));
const ContentPage = lazyLoad(() => import("./pages/ContentPage"));
const OnboardingPage = lazyLoad(() => import("./pages/OnboardingPage"));
const InvitationAcceptance = lazyLoad(() => import("./components/sharing/InvitationAcceptance"));
const GrowthPage = lazyLoad(() => import("./pages/GrowthPage"));
const HealthPage = lazyLoad(() => import("./pages/HealthPage"));
const TrackingPage = lazyLoad(() => import("./pages/TrackingPage"));
const EventsPage = lazyLoad(() => import("./pages/EventsPage"));
const ChildProfilePage = lazyLoad(() => import("./pages/ChildProfilePage"));
const RemindersPage = lazyLoad(() => import("./pages/RemindersPage"));
const ReportsPage = lazyLoad(() => import("./pages/ReportsPage"));
const SleepAnalyticsPage = lazyLoad(() => import("./pages/SleepAnalyticsPage"));

// Initialize performance monitoring
if (import.meta.env.DEV) {
  logBundleInfo();
  monitorMemoryUsage();
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppInner() {
  const { toasts, removeToast } = useToastContext();

  return (
    <Router>
      <div className="min-h-screen bg-bg-primary">
        <div className="fixed top-4 right-20 z-40">
          <WebSocketStatus />
        </div>
        <RealtimeNotifications />
        <SessionWarning />
        <ToastNotifications />
        <HelpCenter />
        <ToastContainer toasts={toasts} onClose={removeToast} />
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/invitations/:token" element={<InvitationAcceptance />} />
            <Route path="/sharing/accept/:token" element={<InvitationAcceptance />} />
            <Route path="/onboarding" element={<ProtectedRoute requireOnboarding={false}><OnboardingPage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/sharing" element={<ProtectedRoute><SharingPage /></ProtectedRoute>} />
            <Route path="/growth" element={<ProtectedRoute><GrowthPage /></ProtectedRoute>} />
            <Route path="/tracking" element={<ProtectedRoute><TrackingPage /></ProtectedRoute>} />
            <Route path="/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
            <Route path="/resources" element={<ProtectedRoute><ContentPage /></ProtectedRoute>} />
            <Route path="/child-profile" element={<ProtectedRoute><ChildProfilePage /></ProtectedRoute>} />
            <Route path="/reminders" element={<ProtectedRoute><RemindersPage /></ProtectedRoute>} />
            <Route path="/health" element={<ProtectedRoute><HealthPage /></ProtectedRoute>} />
            <Route path="/milestones" element={<ProtectedRoute><MilestonesPage /></ProtectedRoute>} />
            <Route path="/content" element={<ProtectedRoute><ContentPage /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
            <Route path="/sleep-analytics" element={<ProtectedRoute><SleepAnalyticsPage /></ProtectedRoute>} />
          </Routes>
        </Layout>
      </div>
    </Router>
  );
}

function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <FeedbackProvider>
          <AuthProvider>
            <ThemeProvider>
              <ChildProvider>
                <WebSocketProvider>
                  <ToastProvider>
                    <AppInner />
                  </ToastProvider>
                </WebSocketProvider>
              </ChildProvider>
            </ThemeProvider>
          </AuthProvider>
        </FeedbackProvider>
      </QueryClientProvider>
    </Provider>
  );
}

export default App;
